/**
 * 営業時間（restaurants.openinghours）の形式検証。
 *
 * 値の単位は「0時からの秒数」（0 〜 86399）。DB には7要素（日曜=0〜土曜=6）の
 * JSON 配列として保存される。null / undefined は 0（=未設定）として扱う。
 *
 * ここでは「時刻として成立しない値」だけを弾く。開始 > 終了やランチ/ディナーの
 * 重複といった意味的な整合性は検査しない（既存データの多くがそれらに該当し、
 * 消費側も実害なく動いているため。2026-09-10 の本番調査で確認）。
 *
 * api / manager（common 経由）どちらからも同じ結果になる純関数。
 */

export type OpeningHoursErrorCode = "OPENINGHOURS-001"; // 形式不正（配列でない/長さ7でない/フィールドが範囲外）

export interface OpeningHoursError {
  code: OpeningHoursErrorCode;
  weekday: number;
}

const SECONDS_PER_DAY = 86400;

const TIME_FIELDS = [
  "lunch_start",
  "lunch_end",
  "dinner_start",
  "dinner_end",
] as const;

const isValidTimeValue = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  (typeof value === "number" &&
    Number.isInteger(value) &&
    value >= 0 &&
    value < SECONDS_PER_DAY);

export const validateOpeningHours = (
  hours: unknown
): OpeningHoursError | null => {
  if (!Array.isArray(hours) || hours.length !== 7) {
    return { code: "OPENINGHOURS-001", weekday: 0 };
  }

  for (let weekday = 0; weekday < hours.length; weekday += 1) {
    const day = hours[weekday];
    if (typeof day !== "object" || day === null || Array.isArray(day)) {
      return { code: "OPENINGHOURS-001", weekday };
    }
    for (const field of TIME_FIELDS) {
      if (!isValidTimeValue((day as Record<string, unknown>)[field])) {
        return { code: "OPENINGHOURS-001", weekday };
      }
    }
  }

  return null;
};
